import { Flex, Box, Text } from "rebass/styled-components";
import styled from "styled-components";
import { useRecoilState } from "recoil";
import { useHistory } from "react-router-dom";
import { BiX } from "react-icons/bi";

import { AllstarModalState } from "MVVM/State/modals";
import { ModalType } from "MVVM/@types";
import { useUser } from "MVVM/Hooks/useUser";
import { canUserCreateCareerMontage } from "MVVM/Utilities/Montage";
import { analytics } from "MVVM/Analytics";

const CreatorCreditsAnnouncement = () => {
  const [allstarModalState, setAllstarModalState] =
    useRecoilState(AllstarModalState);

  const user = useUser();
  const userGroup = user?.allstarUser?.user?.userGroup?.type;

  const shouldShowUpgradeButton = !canUserCreateCareerMontage(userGroup);

  const history = useHistory();

  if (allstarModalState.isOpen !== ModalType.CreatorCreditsAnnouncement)
    return <></>;

  const handleClose = () => {
    setAllstarModalState({
      data: {},
      isOpen: ModalType.None,
      functions: {},
    });
  };

  const handleUpgrade = () => {
    analytics.track("Upgrade CTA Clicked", {
      location: "Creator Credits Announcement Modal",
    });
    history.push("/upgrade");
    handleClose();
  };

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={handleClose}>
          <BiX size={24} />
        </CloseButton>

        <ModalContent>
          <Title>We are phasing out Creator Credits</Title>

          <Description>
            To simplify how Allstar works and continue supporting our dedicated
            creators, we will be focusing on our subscription plans and updating
            montage limitations. To learn more or share feedback{" "}
            <InlineLink
              href="https://www.reddit.com/r/PlayShareStar/comments/1pinnv3/allstar_community_update/"
              target="_blank"
              rel="noopener noreferrer"
            >
              read our Reddit post
            </InlineLink>{" "}
            or{" "}
            <InlineLink
              href="https://help.allstar.gg/hc/en-us/requests/new"
              target="_blank"
              rel="noopener noreferrer"
            >
              open a Support request
            </InlineLink>
            .
          </Description>

          <FeaturesHeading>
            Montage Features by Subscription Tier:
          </FeaturesHeading>

          <TierSection>
            <TierLabel>
              Essentials Tier
              <YourTier>
                {userGroup === "ESSENTIALS" && " (your tier)"}
              </YourTier>
            </TierLabel>
            <FeatureList>
              <FeatureItem>
                <CheckMark>✓</CheckMark>
                <FeatureText>Unlimited Match Montages</FeatureText>
              </FeatureItem>
              <FeatureItem>
                <CheckMark>✓</CheckMark>
                <FeatureText>Unlimited Squad Montages</FeatureText>
              </FeatureItem>
            </FeatureList>
          </TierSection>

          <TierSection>
            <TierLabel>
              Pro Tier & Above
              <YourTier>
                {(userGroup === "PRO" || userGroup === "PRO_PLUS") &&
                  " (your tier)"}
              </YourTier>
            </TierLabel>
            <FeatureList>
              <FeatureItem>
                <CheckMark>✓</CheckMark>
                <FeatureText>All Essentials features</FeatureText>
              </FeatureItem>
              <FeatureItem>
                <CheckMark>✓</CheckMark>
                <FeatureText>Unlimited Career Montages</FeatureText>
              </FeatureItem>
            </FeatureList>
          </TierSection>

          <ButtonGroup>
            {shouldShowUpgradeButton && (
              <ActionButton onClick={handleUpgrade}>Upgrade</ActionButton>
            )}
            <SecondaryButton onClick={handleClose}>Got it</SecondaryButton>
          </ButtonGroup>
        </ModalContent>
      </ModalContainer>
    </ModalOverlay>
  );
};

const ModalOverlay = styled(Box)`
  align-items: center;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  height: 100%;
  justify-content: center;
  left: 0;
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 1000;
`;

const ModalContainer = styled(Box)`
  background: ${({ theme }) => theme.colors.darkerNed};
  border-radius: 16px;
  max-width: 420px;
  padding: 32px;
  position: relative;
  width: 90%;
`;

const CloseButton = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  display: flex;
  justify-content: center;
  padding: 8px;
  position: absolute;
  right: 12px;
  top: 12px;
  transition: color 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.envy};
  }
`;

const ModalContent = styled(Flex)`
  flex-direction: column;
  gap: 20px;
`;

const Title = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 20px;
  font-weight: 700;
  line-height: 1.3;
`;

const Description = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 13px;
  font-weight: 500;
  line-height: 1.5;
`;

const FeaturesHeading = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 16px;
  font-weight: 700;
  line-height: 1.3;
  margin-top: 8px;
`;

const TierSection = styled(Box)`
  margin: 8px 0;
`;

const TierLabel = styled(Text)`
  color: ${({ theme }) => theme.colors.envy};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
  text-transform: uppercase;
`;

const YourTier = styled.span`
  color: ${({ theme }) => theme.colors.envy200});
  font-size: 12px;
  font-weight: 600;
  margin-left: 6px;
`;

const FeatureList = styled(Flex)`
  flex-direction: column;
  gap: 8px;
`;

const FeatureItem = styled(Flex)`
  align-items: flex-start;
  gap: 8px;
`;

const CheckMark = styled(Box)`
  color: ${({ theme }) => theme.colors.envy};
  flex-shrink: 0;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.4;
`;

const FeatureText = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 12px;
  font-weight: 500;
  line-height: 1.4;
`;

const ActionButton = styled.button`
  align-items: center;
  background: ${({ theme }) => theme.colors.spectrum};
  border: none;
  border-radius: 22px;
  color: white;
  cursor: pointer;
  display: flex;
  font-size: 13px;
  font-weight: 600;
  height: 36px;
  justify-content: center;
  margin-top: 12px;
  transition: opacity 0.2s ease;
  width: 100%;

  &:hover {
    opacity: 0.9;
  }

  &:active {
    opacity: 0.8;
  }
`;

const ButtonGroup = styled(Flex)`
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
`;

const SecondaryButton = styled.button`
  align-items: center;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.envy};
  border-radius: 22px;
  color: ${({ theme }) => theme.colors.envy};
  cursor: pointer;
  display: flex;
  font-size: 13px;
  font-weight: 600;
  height: 36px;
  justify-content: center;
  transition: all 0.2s ease;
  width: 100%;

  &:hover {
    background: ${({ theme }) => theme.colors.envy}20;
  }

  &:active {
    opacity: 0.8;
  }
`;

const InlineLink = styled.a`
  color: ${({ theme }) => theme.colors.envy};
  cursor: pointer;
  text-decoration: none;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
    text-decoration: underline;
  }
`;

export { CreatorCreditsAnnouncement };
